import type {
  CadCategory,
  DrawingPreferences,
  IntakeCore,
  IntakeDetails,
  IntakeSpec,
  IntakeSubType,
} from '@shared/types';

type QualityLevel = IntakeSpec['qualityLevel'];

const DEFAULT_SIZES: Record<IntakeSubType, NonNullable<IntakeCore['size']>> = {
  table: { length: 1600, width: 800, height: 740 },
  chair: { length: 520, width: 520, height: 850 },
  shelf: { length: 900, width: 300, height: 1800 },
  bed: { length: 2000, width: 1400, height: 900 },
  desk: { length: 1400, width: 700, height: 740 },
  cabinet: { length: 800, width: 350, height: 900 },
  bracket: { length: 120, width: 80, height: 8 },
  housing: { length: 140, width: 90, height: 50 },
  gear: { length: 60, width: 60, height: 20 },
  shaft: { length: 200, width: 20, height: 20 },
  fastener_part: { length: 20, width: 20, height: 40 },
  fixture: { length: 260, width: 160, height: 70 },
  building: { length: 220, width: 180, height: 120 },
  interior_room: { length: 220, width: 160, height: 80 },
  site: { length: 300, width: 220, height: 40 },
  facade_detail: { length: 160, width: 30, height: 160 },
  decorative: { length: 80, width: 80, height: 120 },
  functional: { length: 80, width: 50, height: 30 },
  replacement: { length: 50, width: 50, height: 30 },
  miniature: { length: 40, width: 40, height: 50 },
  enclosure: { length: 120, width: 80, height: 40 },
};

const DEFAULT_USE: Record<IntakeSubType, string> = {
  table: 'Detailed table design',
  chair: 'Detailed chair design',
  shelf: 'Detailed shelf design',
  bed: 'Detailed bed design',
  desk: 'Detailed desk design',
  cabinet: 'Detailed cabinet design',
  bracket: 'Detailed mounting bracket',
  housing: 'Detailed mechanical housing',
  gear: 'Detailed spur gear',
  shaft: 'Detailed shaft',
  fastener_part: 'Detailed metric fastener',
  fixture: 'Detailed workholding fixture',
  building: 'Detailed architectural building model',
  interior_room: 'Detailed interior room model',
  site: 'Detailed architectural site model',
  facade_detail: 'Detailed facade detail',
  decorative: 'Detailed decorative 3D print',
  functional: 'Detailed functional 3D printed part',
  replacement: 'Detailed replacement part',
  miniature: 'Detailed miniature model',
  enclosure: 'Detailed electronics enclosure',
};

export function defaultIntakeCore(
  category: CadCategory,
  subType: IntakeSubType,
  intendedUse?: string,
): IntakeCore {
  return {
    intendedUse: firstNonEmpty(intendedUse, DEFAULT_USE[subType]),
    size: DEFAULT_SIZES[subType],
    primaryMaterial: defaultMaterial(category),
    fastenerPreference: defaultFastenerPreference(category, subType),
  };
}

export function defaultDrawingPreferences(
  qualityLevel: QualityLevel = 'standard',
  category?: CadCategory,
): DrawingPreferences {
  const manufacturingGrade = qualityLevel === 'manufacturing' || qualityLevel === 'extreme';
  return {
    scope: manufacturingGrade ? 'both' : 'assembly_only',
    sectionCuts: manufacturingGrade ? 'auto' : 'none',
    showHoleTable: manufacturingGrade && category !== 'architecture',
    showBOM: manufacturingGrade,
    toleranceClass: qualityLevel === 'extreme' ? 'precision' : manufacturingGrade ? 'tight' : 'standard',
    jisStrict: false,
  };
}

export function defaultIntakeDetails(category: CadCategory, subType: IntakeSubType): IntakeDetails {
  if (!subTypeBelongsToCategory(category, subType)) {
    throw new Error(`Intake subtype ${subType} is not valid for ${category}`);
  }
  switch (subType) {
    case 'table':
      return {
        category: 'furniture',
        subType: 'table',
        numSeats: 6,
        shape: 'rectangular',
        legStyle: 'straight',
        joineryType: 'mortise_tenon',
        finish: 'oil',
        hasStretcher: false,
        hasApron: true,
      };
    case 'chair':
      return {
        category: 'furniture',
        subType: 'chair',
        chairType: 'dining',
        backStyle: 'slat',
        hasArmrests: false,
        legStyle: 'straight',
        joineryType: 'mortise_tenon',
        finish: 'oil',
      };
    case 'shelf':
      return {
        category: 'furniture',
        subType: 'shelf',
        shelfType: 'bookcase',
        shelfCount: 4,
        adjustableShelves: true,
        hasBack: true,
        joineryType: 'dado',
        finish: 'oil',
      };
    case 'bed':
      return {
        category: 'furniture',
        subType: 'bed',
        bedSize: 'double',
        hasHeadboard: true,
        hasFootboard: false,
        hasUnderStorage: false,
        slatCount: 14,
        joineryType: 'bed_bolt',
        finish: 'oil',
      };
    case 'desk':
      return {
        category: 'furniture',
        subType: 'desk',
        deskType: 'simple',
        drawerCount: 0,
        hasCableManagement: false,
        legStyle: 'straight',
        joineryType: 'mortise_tenon',
        finish: 'oil',
      };
    case 'cabinet':
      return {
        category: 'furniture',
        subType: 'cabinet',
        cabinetType: 'base',
        doorStyle: 'flat_panel',
        doorCount: 2,
        drawerCount: 1,
        shelvesInside: 1,
        hardwareStyle: 'concealed_hinge',
        joineryType: 'dado',
        finish: 'paint',
      };
    case 'bracket':
      return {
        category: 'mechanical',
        subType: 'bracket',
        bracketType: 'L',
        mountingPattern: 'corner',
        holeCount: 4,
        holeSize: 'M5',
        holeFinish: 'through',
        loadDirection: 'shear',
        surfaceFinish: 'as_machined',
      };
    case 'housing':
      return {
        category: 'mechanical',
        subType: 'housing',
        housingType: 'enclosure',
        closure: 'lid_screwed',
        ipRating: 'none',
        internalFeatures: [],
        fastenerSize: 'M3',
        surfaceFinish: 'as_machined',
      };
    case 'gear':
      return {
        category: 'mechanical',
        subType: 'gear',
        gearType: 'spur',
        module: 2,
        toothCount: 20,
        pressureAngle: 20,
        faceWidth: 20,
        boreDiameter: 10,
        hubStyle: 'plain',
      };
    case 'shaft':
      return {
        category: 'mechanical',
        subType: 'shaft',
        shaftType: 'plain',
        diameter: 20,
        keywayPresent: false,
        bearingFit: 'sliding',
        endStyle: 'chamfered',
        surfaceFinish: 'as_machined',
      };
    case 'fastener_part':
      return {
        category: 'mechanical',
        subType: 'fastener_part',
        fastenerType: 'bolt',
        size: 'M6',
        length: 20,
        head: 'hex',
        thread: 'full',
        finish: 'plain',
      };
    case 'fixture':
      return {
        category: 'mechanical',
        subType: 'fixture',
        fixtureType: 'jig',
        workpieceMax: { length: 200, width: 100, height: 50 },
        clampingMethod: 'toggle',
        baseplateMounting: 'tslot',
        toleranceClass: 'standard',
        hasAlignmentPins: false,
      };
    case 'building':
      return {
        category: 'architecture',
        subType: 'building',
        buildingType: 'house',
        scaleRatio: '1:100',
        floorCount: 2,
        roofStyle: 'gable',
        showInterior: false,
        showLandscape: false,
      };
    case 'interior_room':
      return {
        category: 'architecture',
        subType: 'interior_room',
        roomType: 'living',
        scaleRatio: '1:50',
        ceilingHeight: 2400,
        showFurniture: true,
        wallTreatment: 'plain',
        showCeiling: false,
      };
    case 'site':
      return {
        category: 'architecture',
        subType: 'site',
        siteType: 'suburban',
        scaleRatio: '1:500',
        showTopography: false,
        showVegetation: true,
        showRoads: true,
        buildingCount: 1,
      };
    case 'facade_detail':
      return {
        category: 'architecture',
        subType: 'facade_detail',
        detailType: 'window',
        scaleRatio: '1:10',
        material: 'masonry',
        showSection: true,
        showLayers: false,
      };
    case 'decorative':
      return {
        category: 'printing',
        subType: 'decorative',
        itemType: 'vase',
        printer: 'FDM',
        layerHeight: 0.2,
        shellThickness: 1.2,
        supportPolicy: 'avoid',
        vaseModeCompatible: false,
      };
    case 'functional':
      return {
        category: 'printing',
        subType: 'functional',
        functionalRole: 'bracket',
        printer: 'FDM',
        infillPercent: 40,
        shellThickness: 1.6,
        supportPolicy: 'allow_minimal',
        toleranceClass: 'standard',
        insertType: 'none',
      };
    case 'replacement':
      return {
        category: 'printing',
        subType: 'replacement',
        replacingType: 'knob',
        printer: 'FDM',
        matchOriginalDimensions: true,
        toleranceClass: 'standard',
        fastenerInterface: 'none',
        reinforcement: 'none',
      };
    case 'miniature':
      return {
        category: 'printing',
        subType: 'miniature',
        miniType: 'figure',
        scale: '28mm',
        printer: 'SLA',
        layerHeight: 0.05,
        detailLevel: 'high',
        basePresent: true,
      };
    case 'enclosure':
      return {
        category: 'printing',
        subType: 'enclosure',
        enclosureFor: 'electronics',
        printer: 'FDM',
        closure: 'snap_fit',
        insertType: 'none',
        hasVents: false,
        hasCableEntry: true,
        ipRating: 'none',
      };
  }
}

export function completeIntakeDraft(draft: Partial<IntakeSpec>): Partial<IntakeSpec> {
  if (!draft.category || !draft.subType) return draft;

  const qualityLevel = draft.qualityLevel ?? 'standard';
  const intendedUse = firstNonEmpty(
    draft.core?.intendedUse,
    draft.freeform?.slice(0, 120),
    DEFAULT_USE[draft.subType],
  );
  const core = {
    ...defaultIntakeCore(draft.category, draft.subType, intendedUse),
    ...draft.core,
    intendedUse,
  };

  return {
    ...draft,
    qualityLevel,
    core,
    drawing: {
      ...defaultDrawingPreferences(qualityLevel, draft.category),
      ...draft.drawing,
    },
    details: draft.details ?? defaultIntakeDetails(draft.category, draft.subType),
    freeform: draft.freeform ?? '',
  };
}

function defaultMaterial(category: CadCategory): IntakeCore['primaryMaterial'] {
  if (category === 'furniture') return 'plywood';
  if (category === 'mechanical') return 'sheet_metal';
  if (category === 'architecture') return 'mdf';
  return 'printed_pla';
}

function defaultFastenerPreference(
  category: CadCategory,
  subType: IntakeSubType,
): IntakeCore['fastenerPreference'] {
  if (category === 'furniture') {
    return subType === 'table' || subType === 'chair' ? 'mortise_tenon' : 'hidden';
  }
  if (category === 'mechanical') return 'screws';
  if (category === 'printing') return subType === 'enclosure' ? 'screws' : 'none';
  return 'none';
}

function subTypeBelongsToCategory(category: CadCategory, subType: IntakeSubType): boolean {
  if (category === 'furniture') {
    return ['table', 'chair', 'shelf', 'bed', 'desk', 'cabinet'].includes(subType);
  }
  if (category === 'mechanical') {
    return ['bracket', 'housing', 'gear', 'shaft', 'fastener_part', 'fixture'].includes(subType);
  }
  if (category === 'architecture') {
    return ['building', 'interior_room', 'site', 'facade_detail'].includes(subType);
  }
  return ['decorative', 'functional', 'replacement', 'miniature', 'enclosure'].includes(subType);
}

function firstNonEmpty(...values: Array<string | undefined>): string {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return 'Detailed CAD design';
}
